import { useCallback } from 'react';
import getValidateFn from 'js/utils/Validation';
import { useTranslations } from 'js/hooks/i18n';
import { TValidationMethodConfig } from 'js/utils/Validation/types';

import * as helpers from './helpers';
import { useGetFormFieldValue } from '../../../../hooks';

export const useIBANFieldValidation = ({
    fieldName,
    validationConfig
}: {
    fieldName: string;
    validationConfig: TValidationMethodConfig[];
}) => {
    const translate = useTranslations();
    const getFormFieldValue = useGetFormFieldValue();

    const getRecordLevelValidation = useCallback((value: string): Promise<Record<string, string> | null> => {
        const recordLevelValidate = getValidateFn({ validationConfig: { [fieldName]: validationConfig } });

        return recordLevelValidate({ [fieldName]: value });
    }, [fieldName, validationConfig]);

    return useCallback(async (valueRaw = '', formValues: Record<string, string>): Promise<void | string | string[]> => {
        const recordLevelValidation = await getRecordLevelValidation(valueRaw);

        if (recordLevelValidation) {
            return;
        }

        const value = valueRaw.trim();
        const error = translate('playtech.mobile.system.form.iban.internal.doubleCheck');
        const countryCode = getFormFieldValue('countrycode', formValues);

        if (!countryCode || !value || !/\d+/.test(value)) {
            return error;
        }

        const countryCodeNum = Array.from(countryCode)
            .map(char => char.charCodeAt(0) - 55)
            .join('');

        const ibanMod = value.substring(2) + countryCodeNum + value.substring(0, 2);

        return helpers.modulo(ibanMod, 97) === 1
            ? undefined
            : error;
    }, [getFormFieldValue, getRecordLevelValidation, translate]);
};
